import React, { useState, useRef } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '../../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../../components/ui/dialog';
import { Upload, Download, FileSpreadsheet, Check, AlertCircle, Copy, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Location bulk import: sample download + upload with preview/confirm modal.
 * Props: projectId, onImportComplete (callback after successful save)
 */
export const LocationImportModal = ({ projectId, onImportComplete, apiBase = '/electrical-systems' }) => {
  const [preview, setPreview] = useState(null);
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const handleSampleDownload = async () => {
    try {
      const res = await axios.get(`${API}${apiBase}/${projectId}/locations/sample-excel`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = 'location_sample.xlsx'; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Failed to download sample'); }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('Only .xlsx or .xls files are allowed');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}${apiBase}/${projectId}/locations/import-excel`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPreview(res.data);
      setOpen(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to parse Excel file');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleConfirm = async () => {
    if (!preview) return;
    const newNames = preview.preview.filter(p => p.status === 'New').map(p => p.name);
    if (newNames.length === 0) {
      toast.info('No new locations to import');
      setOpen(false);
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(`${API}${apiBase}/${projectId}/locations/bulk-save`, { names: newNames });
      toast.success(`${res.data.saved} locations imported`);
      setOpen(false);
      setPreview(null);
      if (onImportComplete) onImportComplete();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save locations');
    } finally {
      setSaving(false);
    }
  };

  const statusBadge = (status) => {
    switch (status) {
      case 'New':
        return <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full"><Check className="h-3 w-3" />New</span>;
      case 'Already Exists':
        return <span className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full"><Copy className="h-3 w-3" />Already Exists</span>;
      case 'Duplicate in File':
        return <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full"><AlertCircle className="h-3 w-3" />Duplicate in File</span>;
      default:
        return <span className="text-xs text-slate-400">{status}</span>;
    }
  };

  const newCount = preview?.newCount || 0;
  const existsCount = preview?.preview?.filter(p => p.status === 'Already Exists').length || 0;
  const dupCount = preview?.preview?.filter(p => p.status === 'Duplicate in File').length || 0;

  return (
    <>
      <div className="flex items-center gap-1.5">
        <Button variant="outline" size="sm" onClick={handleSampleDownload} data-testid="location-sample-download">
          <Download className="h-3.5 w-3.5 mr-1" />Sample
        </Button>
        <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="location-import-btn">
          {uploading ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Upload className="h-3.5 w-3.5 mr-1" />}
          Import Locations
        </Button>
        <input ref={fileRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={handleFileSelect} data-testid="location-import-input" />
      </div>

      <Dialog open={open} onOpenChange={(v) => { if (!v) { setOpen(false); setPreview(null); } }}>
        <DialogContent className="max-w-lg" data-testid="location-import-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><FileSpreadsheet className="h-5 w-5 text-orange-500" />Import Locations</DialogTitle>
            <DialogDescription>{preview?.totalParsed || 0} locations parsed from Excel</DialogDescription>
          </DialogHeader>

          {/* Summary badges */}
          <div className="flex gap-3 text-xs" data-testid="location-import-summary">
            <span className="text-emerald-700 font-medium">{newCount} New</span>
            <span className="text-slate-500">{existsCount} Already Exist</span>
            {dupCount > 0 && <span className="text-amber-700">{dupCount} Duplicate in File</span>}
          </div>

          {/* Preview table */}
          <div className="border rounded-lg max-h-64 overflow-y-auto" data-testid="location-import-preview">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 sticky top-0">
                <tr>
                  <th className="text-left px-3 py-2 font-medium text-slate-600">Location Name</th>
                  <th className="text-right px-3 py-2 font-medium text-slate-600">Status</th>
                </tr>
              </thead>
              <tbody>
                {preview?.preview?.map((item, i) => (
                  <tr key={i} className="border-t hover:bg-slate-50/50">
                    <td className="px-3 py-1.5">{item.name}</td>
                    <td className="px-3 py-1.5 text-right">{statusBadge(item.status)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setOpen(false); setPreview(null); }} data-testid="location-import-cancel">Cancel</Button>
            <Button onClick={handleConfirm} disabled={saving || newCount === 0} data-testid="location-import-save">
              {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
              {newCount > 0 ? `Save ${newCount} Locations` : 'No New Locations'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
